<!--
  Application shell.

  Owns: boot, the app bar, routing, the notice region and the unsaved-work bar.
  Owns no field: every screen below does its own loading and saving.

  Two rules from the spec are enforced here rather than per screen:

    1. No modal dialogs. Errors, hold-point alarms and unsaved warnings are
       banners in a region that never blocks input, so a user cannot be trapped
       mid-pour.
    2. A client session is read-only end to end. `canEdit` (derived from the
       session role) strips editing controls; the API client refuses mutating
       calls before they are issued; the server refuses them again. The shell's
       job is only to keep the client out of the shift picker, which exists
       solely to stamp entries a client will never make.
-->
<script lang="ts">
  import './app.css';

  import Login from './routes/Login.svelte';
  import ShiftPicker from './routes/ShiftPicker.svelte';
  import ProjectHome from './routes/ProjectHome.svelte';
  import JobSetup from './routes/JobSetup.svelte';
  import PourRecord from './routes/PourRecord.svelte';
  import MixDesignForm from './routes/MixDesignForm.svelte';
  import MixDesignList from './routes/MixDesignList.svelte';
  import SiteList from './routes/quality/SiteList.svelte';
  import SiteForm from './routes/quality/SiteForm.svelte';
  import JhaList from './routes/quality/JhaList.svelte';
  import JhaForm from './routes/quality/JhaForm.svelte';
  import FieldTestList from './routes/quality/FieldTestList.svelte';
  import EquipmentList from './routes/quality/EquipmentList.svelte';
  import DailyReportList from './routes/quality/DailyReportList.svelte';
  import DailyReportForm from './routes/quality/DailyReportForm.svelte';
  import ContractReportList from './routes/quality/ContractReportList.svelte';
  import ContractReportForm from './routes/quality/ContractReportForm.svelte';
  import CementInventory from './routes/quality/CementInventory.svelte';
  import FieldTestForm from './routes/quality/FieldTestForm.svelte';
  import EquipmentForm from './routes/quality/EquipmentForm.svelte';
  import AdminDashboard from './routes/admin/AdminDashboard.svelte';
  import AdminProject from './routes/admin/AdminProject.svelte';
  import AdminRates from './routes/admin/AdminRates.svelte';
  import AdminSites from './routes/admin/AdminSites.svelte';
  import AdminExpenses from './routes/admin/AdminExpenses.svelte';
  import AdminBreakdown from './routes/admin/AdminBreakdown.svelte';

  import {
    SCI_LOGO_ALT,
    SCI_LOGO_DATA_URI,
    SCI_LOGO_HEIGHT,
    SCI_LOGO_WIDTH
  } from './lib/brand';
  import { hasUnsaved, isSaving, online, retryAllUnsaved, unsavedChanges } from './lib/api';
  import { photoStorageProblem, restorePhotoQueue } from './lib/photo';
  import {
    AREA_HOME_PATH,
    isAreaScreen,
    linkHandler,
    location,
    navigate,
    routePath
  } from './lib/router';
  import {
    accountAreaLabel,
    bootState,
    bootstrap,
    dismissNotice,
    isCrew,
    isOffice,
    logout,
    notices,
    notify,
    project,
    session,
    shareLogin,
    viaShareToken
  } from './lib/store';

  /** Set when a share link is rejected, so Login can explain why. */
  let shareError = $state<string | null>(null);
  let shareBusy = $state(false);
  /** Plain guard: one attempt per token, and never inside a reactive read. */
  let attemptedShareToken: string | null = null;
  let retrying = $state(false);

  $effect(() => {
    // Photos captured before a reload, a locked screen or a flat battery are
    // still on the device. Pick the queue up before anything else, so they land
    // without the user having to remember they exist.
    void restorePhotoQueue();
    void bootstrap();
  });

  /**
   * The photo queue's on-device database ran into something the person
   * should be told about, not just something the app quietly worked around
   * (src/lib/photo.ts's `openDb()`, findings I1/I2). Fires once per actual
   * transition — `$effect` only reruns when `$photoStorageProblem`'s VALUE
   * changes, so this cannot spam a banner every time the effect merely
   * re-evaluates. Both messages are phrased as an instruction, not an
   * error: the owner's original complaint was "the add picture doesn't
   * actually work", and a banner that just says something failed would
   * read as confirming it, not fixing it.
   */
  $effect(() => {
    const problem = $photoStorageProblem;
    if (problem === 'blocked') {
      notify({
        tone: 'warn',
        title: 'Photo storage needs a moment',
        detail:
          'Close this app in any other open tabs, or fully close and reopen it, then try adding the photo again.',
        sticky: true
      });
    } else if (problem === 'version-mismatch') {
      notify({
        tone: 'warn',
        title: 'Your photos are waiting',
        detail: "This app version can't read them yet — reload the page to get the latest version.",
        sticky: true
      });
    }
  });

  // Arrival via /share/:code/:token. Exchanges the token for a client session,
  // then rewrites the URL so the token is not left sitting in the address bar.
  $effect(() => {
    const loc = $location;
    if (loc.screen !== 'share') return;
    const { code, token } = loc.params;
    if (!code || !token) return;
    if (attemptedShareToken === token) return;
    attemptedShareToken = token;

    shareBusy = true;
    shareError = null;
    shareLogin(code, token)
      .then(() => navigate(routePath.home(), { replace: true }))
      .catch(() => {
        shareError = 'That link is no longer valid. Ask SCI for a new one, or sign in below.';
      })
      .finally(() => {
        shareBusy = false;
      });
  });

  // Once signed in, /login and /share are no longer meaningful destinations.
  $effect(() => {
    const loc = $location;
    if ($session && (loc.screen === 'login' || loc.screen === 'share') && !shareBusy) {
      navigate(routePath.home(), { replace: true });
    }
  });

  // An office session has no field QC record to show, and reaching a screen
  // outside its own area would call routes it is refused, so send it home to
  // its own area. The area -> path/screens mapping lives in router.ts
  // (AREA_HOME_PATH, isAreaScreen) so a second area adds data there, not a
  // branch here.
  $effect(() => {
    if ($isOffice && $session?.area && !isAreaScreen($session.area, screen)) {
      navigate(AREA_HOME_PATH[$session.area], { replace: true });
    }
  });

  // Mirror of the above: a crew or client session has no office area to
  // show. Send it back to the pour list rather than leaving it on a screen
  // it cannot use.
  $effect(() => {
    if ($session && !$isOffice && onOfficeScreen) navigate(routePath.home(), { replace: true });
  });

  async function doRetry() {
    retrying = true;
    try {
      await retryAllUnsaved();
    } finally {
      retrying = false;
    }
  }

  const needsShift = $derived($session !== null && $isCrew && !$session.shiftName);
  const screen = $derived($location.screen);
  const pourIdParam = $derived($location.params.pourId ?? null);
  const mixIdParam = $derived($location.params.mixId ?? null);
  const jhaIdParam = $derived($location.params.jhaId ?? null);
  const dailyReportIdParam = $derived($location.params.dailyReportId ?? null);
  const contractReportIdParam = $derived($location.params.contractReportId ?? null);
  const fieldTestIdParam = $derived($location.params.fieldTestId ?? null);
  const equipmentIdParam = $derived($location.params.equipmentId ?? null);
  const projectNameParam = $derived($location.params.projectName ?? null);
  const siteIdParam = $derived($location.params.siteId ?? null);
  const onAdminScreen = $derived(isAreaScreen('admin', screen));
  const onMixScreen = $derived(
    screen === 'mixes' || screen === 'mixNew' || screen === 'mixDesign'
  );
  const onQualityScreen = $derived(isAreaScreen('quality', screen));
  const onOfficeScreen = $derived(onMixScreen || onQualityScreen || onAdminScreen);
  /** Where the appbar title sends an office session — its own area's home. */
  const officeHomePath = $derived(
    $session?.area ? AREA_HOME_PATH[$session.area] : routePath.mixes()
  );
</script>

<div class="app">
  {#if $isOffice}
    <header class="appbar appbar--brand">
      <a class="appbar__title" href={officeHomePath} onclick={linkHandler}>
        <strong>{$accountAreaLabel ?? 'SCI'}</strong>
        <span>Sustainable Concrete Inc.</span>
      </a>

      <!--
        The mark, centred in the bar. Centring is done by giving the title and
        the control group an equal `flex: 1 1 0` either side of it, rather than
        by absolute positioning: the two sides are never the same width, and a
        centred overlay would sit on top of the title on a narrow screen. This
        way the title truncates and the mark stays exactly in the middle.
      -->
      <img
        class="appbar__mark"
        src={SCI_LOGO_DATA_URI}
        width={SCI_LOGO_WIDTH}
        height={SCI_LOGO_HEIGHT}
        alt={SCI_LOGO_ALT}
        decoding="async"
      />

      <div class="appbar__actions">
        {#if $isSaving}
          <span class="spinner" aria-label="Saving" title="Saving"></span>
        {:else if !$online}
          <span class="pill pill--warn nowrap">Offline</span>
        {/if}
        <!--
          The title is already a link home, but nothing says so. Deep in a form,
          an explicit Home is what people look for — and it goes to the area's
          own home, not the app root, which an office session cannot reach.
        -->
        <a class="btn btn--sm" href={officeHomePath} onclick={linkHandler}>Home</a>
        <button class="btn btn--sm" type="button" onclick={() => void logout()}>Out</button>
      </div>
    </header>
  {:else if $session}
    <header class="appbar">
      <a class="appbar__title" href={routePath.home()} onclick={linkHandler}>
        <strong>{$project?.name ?? 'SCI Field QC'}</strong>
        <span>
          {$project?.code ?? ''}
          {#if $session.shiftName}· {$session.shiftName}{/if}
          · {$isCrew ? 'SCI crew' : 'Client — read only'}
        </span>
      </a>

      {#if $isSaving}
        <span class="spinner" aria-label="Saving" title="Saving"></span>
      {:else if !$online}
        <span class="pill pill--warn nowrap">Offline</span>
      {/if}

      {#if $isCrew}
        <a class="btn btn--sm" href={routePath.shift()} onclick={linkHandler}>Shift</a>
      {/if}
      <a class="btn btn--sm" href={routePath.home()} onclick={linkHandler}>Home</a>
      <button class="btn btn--sm" type="button" onclick={() => void logout()}>Out</button>
    </header>
  {/if}

  <main class="main" class:main--wide={onOfficeScreen}>
    {#if $notices.length > 0}
      <div class="noticeregion" aria-live="polite">
        {#each $notices as notice (notice.id)}
          <div class="banner banner--{notice.tone}" role={notice.tone === 'error' ? 'alert' : 'status'}>
            <div class="spacer">
              <strong>{notice.title}</strong>
              {#if notice.detail}<span>{notice.detail}</span>{/if}
              {#if notice.actions && notice.actions.length > 0}
                <div class="banner__actions">
                  {#each notice.actions as action (action.label)}
                    <button class="btn btn--sm" type="button" onclick={() => void action.run()}>
                      {action.label}
                    </button>
                  {/each}
                </div>
              {/if}
            </div>
            <button
              class="btn btn--sm btn--ghost"
              type="button"
              aria-label="Dismiss"
              onclick={() => dismissNotice(notice.id)}
            >
              ✕
            </button>
          </div>
        {/each}
      </div>
    {/if}

    {#if $bootState === 'loading'}
      <p class="meta center boot"><span class="spinner" aria-hidden="true"></span> Starting…</p>
    {:else if $bootState === 'error'}
      <div class="banner banner--error" role="alert">
        <div class="spacer">
          <strong>Cannot reach the server</strong>
          Anything you have already typed is safe on this device.
          <div class="banner__actions">
            <button class="btn btn--sm" type="button" onclick={() => void bootstrap()}>
              Try again
            </button>
          </div>
        </div>
      </div>
    {:else if shareBusy}
      <p class="meta center boot"><span class="spinner" aria-hidden="true"></span> Opening the record…</p>
    {:else if onOfficeScreen}
      {#if !$session}
        <!--
          A signed-out visit to /mixes or /quality shows the same login as
          everywhere else. The URL never changes here, so once the account
          login succeeds `$isOffice` flips true and this block re-renders
          straight into the right area below — that round trip back to the
          same address is the point of a single login screen.
        -->
        <Login initialCode={$location.params.code ?? ''} message={shareError} />
      {:else if !$isOffice || !$session.area || !isAreaScreen($session.area, screen)}
        <!-- A crew/client session, or an office session whose own area does
             not own this screen, lands here only for the instant before the
             redirect effect above sends it home; nothing to render. -->
      {:else if screen === 'mixNew'}
        <MixDesignForm mixId={null} />
      {:else if screen === 'mixDesign' && mixIdParam !== null}
        {#key mixIdParam}
          <MixDesignForm mixId={mixIdParam} />
        {/key}
      {:else if screen === 'mixes'}
        <MixDesignList />
      {:else if screen === 'quality'}
        <SiteList />
      {:else if screen === 'qualitySiteForm'}
        {#key siteIdParam}
          <SiteForm siteId={siteIdParam} />
        {/key}
      {:else if screen === 'qualityJhaList'}
        <JhaList />
      {:else if screen === 'qualityFieldTestList'}
        <FieldTestList />
      {:else if screen === 'qualityEquipmentList'}
        <EquipmentList />
      {:else if screen === 'qualityDailyReportList'}
        <DailyReportList />
      {:else if screen === 'qualityContractReportList'}
        <ContractReportList />
      {:else if screen === 'qualityCement'}
        <CementInventory />
      {:else if screen === 'qualityJhaForm'}
        {#key jhaIdParam}
          <JhaForm jhaId={jhaIdParam} />
        {/key}
      {:else if screen === 'qualityDailyReportForm'}
        {#key dailyReportIdParam}
          <DailyReportForm dailyReportId={dailyReportIdParam} />
        {/key}
      {:else if screen === 'qualityContractReportForm'}
        {#key contractReportIdParam}
          <ContractReportForm contractReportId={contractReportIdParam} />
        {/key}
      {:else if screen === 'qualityFieldTestForm'}
        {#key fieldTestIdParam}
          <FieldTestForm fieldTestId={fieldTestIdParam} />
        {/key}
      {:else if screen === 'qualityEquipmentForm'}
        {#key equipmentIdParam}
          <EquipmentForm equipmentId={equipmentIdParam} />
        {/key}
      {:else if screen === 'admin'}
        <AdminDashboard />
      {:else if screen === 'adminRates'}
        <AdminRates />
      {:else if screen === 'adminSites'}
        <AdminSites />
      {:else if screen === 'adminExpenses'}
        <AdminExpenses />
      {:else if screen === 'adminBreakdown'}
        <AdminBreakdown />
      {:else if screen === 'adminProject' && projectNameParam !== null}
        {#key projectNameParam}
          <AdminProject projectName={projectNameParam} />
        {/key}
      {/if}
    {:else if !$session}
      <Login initialCode={$location.params.code ?? ''} message={shareError} />
    {:else if $isOffice}
      <!-- An office session on a field QC screen lands here only for the
           instant before the redirect effect above sends it to its own
           area's home; nothing to render. The mirror of the office branch's
           own transient arm. Without this, ProjectHome mounted for that one
           render pass and its loadPours() was (rightly) refused — putting a
           "Could not load the pour list" banner on every office login. -->
    {:else if needsShift}
      <ShiftPicker />
    {:else if screen === 'shift'}
      <ShiftPicker />
    {:else if screen === 'jobSetup'}
      <JobSetup />
    {:else if screen === 'pour' && pourIdParam !== null}
      {#key pourIdParam}
        <PourRecord pourId={pourIdParam} />
      {/key}
    {:else if screen === 'notFound'}
      <div class="card">
        <div class="card__body">
          <h1>Not found</h1>
          <p class="meta">That address does not match anything in this app.</p>
          <a class="btn btn--primary" href={routePath.home()} onclick={linkHandler}>Go to pours</a>
        </div>
      </div>
    {:else}
      <ProjectHome />
    {/if}
  </main>

  {#if $hasUnsaved}
    <!--
      The unsaved bar. It is informational and dismissible by fixing the
      problem, never by trapping the user: the record underneath stays fully
      editable while it is showing.
    -->
    <div class="savebar" role="status">
      <div class="spacer">
        <strong>{$unsavedChanges.length} change{$unsavedChanges.length === 1 ? '' : 's'} not saved</strong>
        <span class="meta savebar__detail">
          {$unsavedChanges.map((entry) => entry.label).join(', ')} — kept on this device.
        </span>
      </div>
      <button class="btn btn--sm btn--primary" type="button" disabled={retrying} onclick={doRetry}>
        {#if retrying}<span class="spinner" aria-hidden="true"></span> Retrying…{:else}Retry{/if}
      </button>
    </div>
  {:else if $isSaving}
    <div class="savebar savebar--saving" role="status">
      <span class="spinner" aria-hidden="true"></span>
      <span>Saving…</span>
    </div>
  {/if}

  {#if $viaShareToken}
    <p class="visually-hidden">Read-only client view.</p>
  {/if}
</div>

<style>
  .appbar__title {
    color: inherit;
    text-decoration: none;
  }

  /*
    Three groups across the bar: title, mark, controls. The outer two both get
    `flex: 1 1 0` so they always claim the same width, which is what puts the
    mark in the exact middle whatever the title says or however many controls
    are showing.
  */
  .appbar--brand .appbar__title {
    flex: 1 1 0;
  }

  .appbar__actions {
    flex: 1 1 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.6rem;
  }

  .appbar__mark {
    flex: 0 0 auto;
    height: 1.6rem;
    width: auto;
    /*
      The lockup is brand blue and the bar is brand navy — on that ground the
      wordmark is close to unreadable. Flattening it to white (black, then
      inverted) keeps the artwork's own edges and antialiasing, which is why
      this is done with a filter rather than by shipping a second image that
      would then have to be kept in step with ../brand.ts.
    */
    filter: brightness(0) invert(1);
  }

  /* On a phone the bar is tight; shrink the mark rather than let it crowd the
     title into nothing. Height only, so the lockup is never distorted. */
  @media (max-width: 30rem) {
    .appbar__mark {
      height: 1.15rem;
    }
  }

  .boot {
    padding: 3rem 0;
  }

  .noticeregion {
    margin-bottom: var(--gap);
  }

  .savebar__detail {
    display: block;
    font-weight: 400;
  }
</style>
